/**
 * The words the interface uses, in one place.
 *
 * The app grew its vocabulary from its implementation: "header pack", "corpus",
 * "basket", "detached", "label", "snapshot". Those are accurate and mean nothing to a
 * tutor who has never written LaTeX. Every page takes its wording from here, so a term
 * is renamed once and stays consistent — and the glossary on the Help page is generated
 * from the same table, so it cannot drift from what the screens say.
 *
 * Every table exists in English and German (Phase 7, Lane I): `localized()` puts both
 * behind the one export, and reading a key gives the current language's word. Read
 * them while rendering, not into module-level constants (see i18n/index.ts). The
 * GitLab tables used only on the power pages (the Integrations page, the publish
 * drawer's conflict cards, Admin) stay English, as those pages do.
 *
 * API field names do not change: `headerName`, `detached` and the rest stay as they are.
 */
import type { CourseFunction, GitlabFileRole, GitlabPipelineStatus, SheetStatus, SyncState } from '@studio/shared';
import { localized, type Translation } from './i18n';

const L_EN = {
  home: 'Home',
  sheets: 'Sheets',
  /**
   * The one place exercises live, whatever their source (one model of exercises, 2026-10).
   * `findExercises` and `myExercises` are the two pages it replaced, kept as names of it.
   */
  exercises: 'Exercises',
  findExercises: 'Exercises',
  myExercises: 'Exercises',
  review: 'Review',
  templates: 'Sheet templates',
  integrations: 'GitLab & API',
  help: 'Help & glossary',

  picks: 'Your picks',
  addToPicks: 'Add to picks',
  inPicks: 'In your picks',
  makeSheetFromPicks: 'Make a sheet from picks',
  addToSheet: 'Add to a sheet…',

  /** the sources of exercises, as the Exercises page's tabs name them */
  pastExercises: 'Past sheets 2016–2025',
  writtenByTutors: 'Written in Studio',
  /** the 2026 course repository's exercises, imported from GitLab (read-only) */
  courseRepoExercises: 'Course repository',
  /** Phase 8: the 2026 Typst archive's exercises no 2025 exercise matches (read-only) */
  archiveUnused: '2026 Typst archive',
  /** a corpus or archive exercise someone edited: its one shared copy written in Studio */
  editedVersion: 'Edited version',
  assignedToMe: 'Assigned to me',
  createdByMe: 'Created by me',
  newExercise: 'New exercise',
  /** a 2025 exercise's port in the 2026 Typst archive */
  typstVersion: 'Typst version',
  typstArchive: 'Typst (2026 archive)',
  pastTerms: 'Past sheets',
  thisTerm: 'This term',
  unassigned: 'No term set',

  sheetNumber: 'Sheet number',
  numberingPrefix: 'Numbering prefix',
  sheetType: 'Sheet type',
  template: 'Sheet template',
  exerciseVariant: 'Variant of an exercise',
  history: 'History',
  problems: 'Reported problems',
  studentVersion: 'Student version',
  withSolutions: 'With solutions',
  markingVersion: 'Marking scheme',
  courseTemplate: '2026 course template',
  newSheet: 'New sheet',
  /** linked sheets (Phase 7): the same sheet in the other language, or for another group */
  twin: 'Twin',
  variant: 'Variant',
} as const;

const L_DE: Translation<typeof L_EN> = {
  home: 'Start',
  sheets: 'Blätter',
  exercises: 'Aufgaben',
  findExercises: 'Aufgaben',
  myExercises: 'Aufgaben',
  review: 'Prüfen',
  templates: 'Blattvorlagen',
  integrations: 'GitLab & API',
  help: 'Hilfe & Glossar',

  picks: 'Deine Auswahl',
  addToPicks: 'Zur Auswahl',
  inPicks: 'In deiner Auswahl',
  makeSheetFromPicks: 'Blatt aus der Auswahl machen',
  addToSheet: 'Zu einem Blatt hinzufügen…',

  pastExercises: 'Frühere Blätter 2016–2025',
  writtenByTutors: 'In Studio geschrieben',
  courseRepoExercises: 'Kurs-Repository',
  archiveUnused: 'Typst-Archiv 2026',
  editedVersion: 'Bearbeitete Fassung',
  assignedToMe: 'Mir zugewiesen',
  createdByMe: 'Von mir erstellt',
  newExercise: 'Neue Aufgabe',
  typstVersion: 'Typst-Fassung',
  typstArchive: 'Typst (Archiv 2026)',
  pastTerms: 'Frühere Blätter',
  thisTerm: 'Dieses Semester',
  unassigned: 'Kein Semester gesetzt',

  sheetNumber: 'Blattnummer',
  numberingPrefix: 'Nummerierungspräfix',
  sheetType: 'Blattart',
  template: 'Blattvorlage',
  exerciseVariant: 'Variante einer Aufgabe',
  history: 'Verlauf',
  problems: 'Gemeldete Probleme',
  studentVersion: 'Studierendenfassung',
  withSolutions: 'Mit Lösungen',
  markingVersion: 'Bewertungsschema',
  courseTemplate: 'Kursvorlage 2026',
  newSheet: 'Neues Blatt',
  twin: 'Zwilling',
  variant: 'Variante',
};

export const L = localized('L', { en: L_EN, de: L_DE });

/** Sheet types as tutors say them; the stored `SheetKind` values stay unchanged. */
const SHEET_TYPES_EN = [
  { kind: 'tutorial', label: 'Tutorial sheet', hint: 'Worked on together in the tutorial.' },
  { kind: 'exercise', label: 'Exercise sheet', hint: 'Homework handed in and marked.' },
  { kind: 'exam-prep', label: 'Exam preparation', hint: 'Revision before an exam.' },
  { kind: 'mock-exam', label: 'Mock exam', hint: 'An exam under exam conditions.' },
  { kind: 'cards', label: 'Cards', hint: 'KaFe cards and other card sets.' },
  { kind: 'other', label: 'Other', hint: 'Explanations, handouts, anything else.' },
] as const;

export const SHEET_TYPES = localized('SHEET_TYPES', {
  en: SHEET_TYPES_EN,
  de: [
    { kind: 'tutorial', label: 'Tutoriumsblatt', hint: 'Wird im Tutorium gemeinsam bearbeitet.' },
    { kind: 'exercise', label: 'Übungsblatt', hint: 'Hausaufgaben, die abgegeben und bewertet werden.' },
    { kind: 'exam-prep', label: 'Klausurvorbereitung', hint: 'Wiederholung vor einer Klausur.' },
    { kind: 'mock-exam', label: 'Probeklausur', hint: 'Eine Klausur unter Klausurbedingungen.' },
    { kind: 'cards', label: 'Karten', hint: 'KaFe-Karten und andere Kartensätze.' },
    { kind: 'other', label: 'Sonstiges', hint: 'Erklärungen, Handouts, alles andere.' },
  ],
});

export const sheetTypeLabel = (kind: string): string =>
  SHEET_TYPES.find((t) => t.kind === kind)?.label ?? kind;

/**
 * A sheet type's English name whatever the interface language: what goes on paper
 * (a sheet's printed head is course content, and the course writes it in English).
 */
export const englishSheetTypeLabel = (kind: string): string =>
  SHEET_TYPES_EN.find((t) => t.kind === kind)?.label ?? kind;

/**
 * The 2026 course repository's sheet templates, as tutors call them. The stored value
 * is the Typst function name (`meta.courseFunction`); these words are only the screen's.
 */
export const COURSE_FUNCTION_LABELS: Record<CourseFunction, string> = localized('COURSE_FUNCTION_LABELS', {
  en: {
    'tutorial-sheet': 'Tutorial sheet',
    'exercise-sheet': 'Exercise sheet',
    'exam-preparation-sheet': 'Exam preparation',
    'extra-tutorial-sheet': 'Extra tutorial',
    'rep-tutorial-sheet': 'Repetition tutorial',
    'exam-task-sheet': 'Exam task sheet',
    'explanation-sheet': 'Explanation',
    'knobel-sheet': 'kNobel',
  },
  de: {
    'tutorial-sheet': 'Tutoriumsblatt',
    'exercise-sheet': 'Übungsblatt',
    'exam-preparation-sheet': 'Klausurvorbereitung',
    'extra-tutorial-sheet': 'Zusatztutorium',
    'rep-tutorial-sheet': 'Wiederholungstutorium',
    'exam-task-sheet': 'Klausuraufgabenblatt',
    'explanation-sheet': 'Erklärung',
    'knobel-sheet': 'kNobel',
  },
});

/** Old term → new term, with a one-line explanation. Rendered on the Help page. */
export const GLOSSARY: readonly { old: string; now: string; meaning: string }[] = localized('GLOSSARY', {
  en: [
    { old: 'Header pack', now: L_EN.templates, meaning: 'The page frame a sheet is printed in: title block, logo, fonts, macros.' },
    { old: 'Find exercises / My exercises', now: L_EN.exercises, meaning: 'The one place every exercise lives, whatever its source: search it, open one, edit it, see who works on what (Assigned to me, Created by me).' },
    { old: 'Corpus / Exercise library', now: `${L_EN.exercises} → ${L_EN.pastExercises}`, meaning: 'Every exercise from the 2016–2025 sheets, searchable and ready to reuse.' },
    { old: 'Written by tutors / Written here', now: L_EN.writtenByTutors, meaning: 'Exercises written in Sheet Studio rather than taken from an old sheet. Every tutor may change a shared one; its history keeps every version.' },
    { old: 'Superseded / copy of an exercise', now: L_EN.editedVersion, meaning: 'An exercise from the course repositories cannot change itself: editing its text makes one shared edited version, which everyone then uses in its place.' },
    { old: '2026 (GitLab) / git/…', now: L_EN.courseRepoExercises, meaning: 'Exercises of the 2026 course repository, imported from GitLab by an admin. Changed in the repository, not here; on a sheet they are an ordinary Typst exercise.' },
    { old: 'archive/2026', now: L_EN.typstVersion, meaning: `The course repository's Typst port of a 2025 exercise (archive/2026). Shown on the exercise as "${L_EN.typstArchive}", with its source and a preview.` },
    { old: 'Pool 2025 (Typst, unused) / a26/…', now: L_EN.archiveUnused, meaning: 'Exercises of the 2025 pool that no 2025 sheet in Sheet Studio has, in their Typst port. Their details can be changed; editing the text makes an edited version. They go on a sheet as Typst.' },
    { old: 'Basket / selection', now: L_EN.picks, meaning: 'Exercises you collected while browsing, waiting to go on a sheet.' },
    { old: 'Label', now: L_EN.sheetNumber, meaning: 'What the sheet is called on paper: A, B, 3 …' },
    { old: 'Sheet type letter', now: L_EN.numberingPrefix, meaning: 'The letter before the number in exercise numbers: the T in TB.3.' },
    { old: 'Detached / Edited for this sheet / Own wording', now: L_EN.exerciseVariant, meaning: 'A sheet has no wording of its own: an item is its exercise. Another version for some sheets is a variant, a new exercise beside the original.' },
    { old: 'Snapshots', now: L_EN.history, meaning: 'Saved versions of a sheet you can look at and restore.' },
    { old: 'Issues / annotations', now: L_EN.problems, meaning: 'Mistakes or doubts someone recorded about an exercise.' },
    { old: 'Past terms / Archive', now: `${L_EN.sheets} → ${L_EN.pastTerms}`, meaning: 'The sheets of earlier years, to browse and reuse.' },
    { old: 'Student / With solution', now: `${L_EN.studentVersion} / ${L_EN.withSolutions}`, meaning: 'The two PDFs every sheet has: without and with sample solutions.' },
    { old: 'Marking', now: L_EN.markingVersion, meaning: `A third PDF that sheets on the ${L_EN.courseTemplate} have: the solutions plus the marking notes, for tutors only.` },
    { old: 'course/prog1-2026', now: L_EN.courseTemplate, meaning: 'The sheet is written the way the 2026 course repository lays out its sheets, and built with its Typst headers — what the preview shows is what the course CI builds.' },
    // Phase 7, Lane L: linked sheets (Sheet details → Linked sheets)
    { old: 'Translated copy', now: L_EN.twin, meaning: 'The same sheet in the other language, made from Sheet details → Linked sheets: exercises replaced by their translations, the rest marked to translate. It shows what changed in the original since.' },
    { old: 'Copy for another group', now: L_EN.variant, meaning: 'The same sheet for another group, with some exercises swapped for other versions no sheet of the term uses. It too shows what changed in the original since.' },
  ],
  de: [
    { old: 'Header-Paket (Header pack)', now: L_DE.templates, meaning: 'Der Rahmen, in dem ein Blatt gedruckt wird: Titelblock, Logo, Schriften, Makros.' },
    { old: 'Aufgaben finden / Meine Aufgaben', now: L_DE.exercises, meaning: 'Der eine Ort, an dem jede Aufgabe liegt, egal woher sie stammt: suchen, öffnen, bearbeiten, sehen, wer woran arbeitet (Mir zugewiesen, Von mir erstellt).' },
    { old: 'Korpus / Aufgabenbibliothek', now: `${L_DE.exercises} → ${L_DE.pastExercises}`, meaning: 'Jede Aufgabe der Blätter von 2016–2025, durchsuchbar und bereit zur Wiederverwendung.' },
    { old: 'Von Tutor*innen geschrieben / Hier geschrieben', now: L_DE.writtenByTutors, meaning: 'Aufgaben, die in Sheet Studio geschrieben wurden, statt von einem alten Blatt zu stammen. Alle Tutor*innen dürfen eine geteilte ändern; ihr Verlauf behält jede Fassung.' },
    { old: 'Ersetzt / Kopie einer Aufgabe', now: L_DE.editedVersion, meaning: 'Eine Aufgabe aus den Kurs-Repositorys kann sich nicht selbst ändern: Wer ihren Text bearbeitet, macht eine gemeinsame bearbeitete Fassung, die dann alle an ihrer Stelle verwenden.' },
    { old: '2026 (GitLab) / git/…', now: L_DE.courseRepoExercises, meaning: 'Aufgaben aus dem Kurs-Repository 2026, von einer Admin-Person aus GitLab importiert. Geändert werden sie im Repository, nicht hier; auf einem Blatt eine gewöhnliche Typst-Aufgabe.' },
    { old: 'archive/2026', now: L_DE.typstVersion, meaning: `Die Typst-Übertragung einer Aufgabe von 2025 im Kurs-Repository (archive/2026). An der Aufgabe als „${L_DE.typstArchive}“ zu sehen, mit Quelltext und Vorschau.` },
    { old: 'Pool 2025 (Typst, ungenutzt) / a26/…', now: L_DE.archiveUnused, meaning: 'Aufgaben aus dem Pool 2025, die kein Blatt von 2025 in Sheet Studio enthält, in ihrer Typst-Übertragung. Ihre Angaben lassen sich ändern; wer den Text bearbeitet, macht eine bearbeitete Fassung. Auf einem Blatt sind sie Typst.' },
    { old: 'Korb / Auswahl (Basket)', now: L_DE.picks, meaning: 'Aufgaben, die du beim Stöbern gesammelt hast und die auf ein Blatt sollen.' },
    { old: 'Label', now: L_DE.sheetNumber, meaning: 'Wie das Blatt auf Papier heißt: A, B, 3 …' },
    { old: 'Buchstabe der Blattart', now: L_DE.numberingPrefix, meaning: 'Der Buchstabe vor der Nummer in Aufgabennummern: das T in TB.3.' },
    { old: 'Losgelöst / Für dieses Blatt bearbeitet / Eigene Formulierung', now: L_DE.exerciseVariant, meaning: 'Ein Blatt hat keine eigene Formulierung: Ein Eintrag ist seine Aufgabe. Eine andere Fassung für manche Blätter ist eine Variante, eine neue Aufgabe neben dem Original.' },
    { old: 'Schnappschüsse (Snapshots)', now: L_DE.history, meaning: 'Gespeicherte Fassungen eines Blatts, die du ansehen und wiederherstellen kannst.' },
    { old: 'Issues / Anmerkungen', now: L_DE.problems, meaning: 'Fehler oder Zweifel, die jemand zu einer Aufgabe notiert hat.' },
    { old: 'Frühere Semester / Archiv', now: `${L_DE.sheets} → ${L_DE.pastTerms}`, meaning: 'Die Blätter früherer Jahre, zum Stöbern und Wiederverwenden.' },
    { old: 'Student / With solution', now: `${L_DE.studentVersion} / ${L_DE.withSolutions}`, meaning: 'Die zwei PDFs, die jedes Blatt hat: ohne und mit Musterlösungen.' },
    { old: 'Marking', now: L_DE.markingVersion, meaning: `Ein drittes PDF, das Blätter mit der ${L_DE.courseTemplate} haben: die Lösungen plus die Bewertungshinweise, nur für Tutor*innen.` },
    { old: 'course/prog1-2026', now: L_DE.courseTemplate, meaning: 'Das Blatt ist so geschrieben, wie das Kurs-Repository 2026 seine Blätter aufbaut, und wird mit dessen Typst-Headern gebaut — die Vorschau zeigt, was die Kurs-CI baut.' },
    { old: 'Übersetzte Kopie', now: L_DE.twin, meaning: 'Dasselbe Blatt in der anderen Sprache, erstellt unter Blattdetails → Verknüpfte Blätter: Aufgaben durch ihre Übersetzungen ersetzt, der Rest zum Übersetzen markiert. Es zeigt, was sich seitdem im Original geändert hat.' },
    { old: 'Kopie für eine andere Gruppe', now: L_DE.variant, meaning: 'Dasselbe Blatt für eine andere Gruppe, mit einigen Aufgaben gegen andere Fassungen getauscht, die kein Blatt des Semesters verwendet. Auch es zeigt, was sich seitdem im Original geändert hat.' },
  ],
});

/**
 * What to call a sheet that has no title: its type and number. A title is optional — the
 * printed head already says "Tutorial Sheet A" — and filling it with the same words made
 * the PDF say it twice.
 */
export const sheetDisplayTitle = (s: { title?: string | null; kind: string; label: string }): string =>
  s.title?.trim() || `${sheetTypeLabel(s.kind)} ${s.label}`;

/* ------------------------------------------------------------------- GitLab */

/**
 * A sheet's state in the course repository, as tutors say it — no "push", "rebase" or
 * "merge request" on the badge. Used by the Integrations page's sync table.
 */
export const SYNC_STATE_LABELS: Record<SyncState, string> = localized('SYNC_STATE_LABELS', {
  en: {
    'not-linked': 'Not in GitLab',
    published: 'Published',
    'local-changes': 'Changes not in GitLab yet',
    'remote-changes': 'Changed in GitLab',
    conflict: 'Conflict',
    review: 'In review',
    publishing: 'Sending…',
    error: 'Problem',
  },
  de: {
    'not-linked': 'Nicht in GitLab',
    published: 'Veröffentlicht',
    'local-changes': 'Änderungen noch nicht in GitLab',
    'remote-changes': 'In GitLab geändert',
    conflict: 'Konflikt',
    review: 'Im Review',
    publishing: 'Wird gesendet…',
    error: 'Problem',
  },
});

/** GitLab's pipeline states, as what they mean for the sheet's PDFs. */
export const PIPELINE_LABELS: Record<GitlabPipelineStatus, string> = localized('PIPELINE_LABELS', {
  en: {
    created: 'CI waiting',
    waiting_for_resource: 'CI waiting',
    preparing: 'CI waiting',
    pending: 'CI waiting',
    scheduled: 'CI waiting',
    running: 'CI running',
    success: 'CI passed',
    failed: 'CI failed',
    canceled: 'CI cancelled',
    skipped: 'CI skipped',
    manual: 'CI needs a manual start',
  },
  de: {
    created: 'CI wartet',
    waiting_for_resource: 'CI wartet',
    preparing: 'CI wartet',
    pending: 'CI wartet',
    scheduled: 'CI wartet',
    running: 'CI läuft',
    success: 'CI erfolgreich',
    failed: 'CI fehlgeschlagen',
    canceled: 'CI abgebrochen',
    skipped: 'CI übersprungen',
    manual: 'CI muss von Hand gestartet werden',
  },
});

/** The course repository's words on the Integrations page (a power page: English only). */
export const GITLAB_LABELS = {
  courseRepository: 'Course repository',
  prepareRepository: 'Prepare the repository',
  updateTemplate: 'Update the course template from GitLab',
  importFromRepository: 'Import from the course repository…',
  publishedSheets: 'Sheets in GitLab',
} as const;

/** Publishing a sheet: the editor's badge, its drawer and the Download menu. */
export const PUBLISH_LABELS = localized('PUBLISH_LABELS', {
  en: {
    publishMenu: 'Course repository (GitLab)…',
    drawerTitle: 'Course repository (GitLab)',
    publishForReview: 'Open a review (merge request)',
    updateReview: 'Update the review',
    publishToMain: 'Publish directly to main…',
    confirmToMain: 'Publish to main now',
    checkBuilds: 'Check that it builds',
    openReview: 'open it in GitLab',
    ciPdfs: 'PDFs built by the course CI',
  },
  de: {
    publishMenu: 'Kurs-Repository (GitLab)…',
    drawerTitle: 'Kurs-Repository (GitLab)',
    publishForReview: 'Review öffnen (Merge-Request)',
    updateReview: 'Review aktualisieren',
    publishToMain: 'Direkt auf main veröffentlichen…',
    confirmToMain: 'Jetzt auf main veröffentlichen',
    checkBuilds: 'Prüfen, ob es baut',
    openReview: 'in GitLab öffnen',
    ciPdfs: 'Von der Kurs-CI gebaute PDFs',
  },
});

/** Changes made in GitLab, and importing from the course repository (power pages: English only). */
export const SYNC_LABELS = {
  changedInGitlab: 'Changed in GitLab',
  useGitlab: 'Use the GitLab version',
  keepMine: 'Keep mine',
  mergeBoth: 'Merge both',
  generatedFile: 'Studio writes this file from the sheet, so a change made to it in GitLab cannot be taken over. Keep your version; the next publish writes it again.',
  importTitle: 'Import from the course repository',
  importSheet: 'Import as a sheet',
  importSheetsTab: 'A sheet',
  importLibraryTab: 'Exercises for the library',
  importLibrary: 'Import exercises into the library',
} as const;

/** What a file in a published sheet folder is, for the list of changes (power pages: English only). */
export const FILE_ROLE_LABELS: Record<GitlabFileRole, string> = {
  main: 'Sheet',
  exercise: 'Exercise',
  asset: 'Image',
  embed: 'Embedded PDF',
  'embed-source': 'LaTeX of an embedded PDF',
  meta: 'Studio’s notes (studio.json)',
};

/** The week model: weeks, a sheet's status, the bin and the Admin page (Phase 6). */
const WEEK_LABELS_EN = {
  week: 'Week',
  /** a calendar week without tutorials (the Christmas break) */
  noTutorials: 'No tutorials',
  thisWeek: 'This week',
  /** the week being prepared when today is before it: before the term, or in a break */
  nextWeek: 'Next week',
  noWeek: 'No week set',
  bin: 'Bin',
  moveToBin: 'Move to bin',
  restore: 'Restore',
  purge: 'Delete for good',
  /** how long a binned sheet stays restorable */
  binHint: 'Sheets in the bin can be restored for 30 days; then they are deleted for good.',
  admin: 'Admin',
  adminOnly: 'Only admins can open this page.',
} as const;

const WEEK_LABELS_DE: Translation<typeof WEEK_LABELS_EN> = {
  week: 'Woche',
  noTutorials: 'Keine Tutorien',
  thisWeek: 'Diese Woche',
  nextWeek: 'Nächste Woche',
  noWeek: 'Keine Woche gesetzt',
  bin: 'Papierkorb',
  moveToBin: 'In den Papierkorb',
  restore: 'Wiederherstellen',
  purge: 'Endgültig löschen',
  binHint: 'Blätter im Papierkorb lassen sich 30 Tage lang wiederherstellen; danach werden sie endgültig gelöscht.',
  admin: 'Admin',
  adminOnly: 'Nur Admins können diese Seite öffnen.',
};

export const WEEK_LABELS = localized('WEEK_LABELS', { en: WEEK_LABELS_EN, de: WEEK_LABELS_DE });

/** Draft → Ready → Published, as tutors say it; the stored `SheetStatus` values stay unchanged. */
export const STATUS_LABELS: Record<SheetStatus, string> = localized('STATUS_LABELS', {
  en: {
    draft: 'Draft',
    ready: 'Ready',
    published: 'Published',
  },
  de: {
    draft: 'Entwurf',
    ready: 'Fertig',
    published: 'Veröffentlicht',
  },
});

/** Home and the Sheets page's views (Phase 6). */
export const HOME_LABELS = localized('HOME_LABELS', {
  en: {
    continue: 'Continue',
    needsAttention: 'Needs attention',
    nothingNeedsAttention: 'Nothing needs your attention.',
    recentSheets: 'Your recent sheets',
    reuseLastYear: 'Reuse last year’s week',
    pickOnTopics: 'Pick exercises on',
    startEmpty: 'Start empty',
    sameWeekPastYears: 'Same week in past years',
    suggestedExercises: 'Suggested exercises',
    typicalTopics: 'Typical topics',
    useAsStart: 'Use as a start',
    noCalendar: 'This term has no calendar yet, so weeks have no dates. An admin sets it on the Admin page.',
    introSteps: ['Find exercises', 'Arrange them on a sheet', 'Print the Student and Solutions PDFs'],
  },
  de: {
    continue: 'Weitermachen',
    needsAttention: 'Braucht Aufmerksamkeit',
    nothingNeedsAttention: 'Gerade braucht nichts deine Aufmerksamkeit.',
    recentSheets: 'Deine letzten Blätter',
    reuseLastYear: 'Aus dem Vorjahr wiederverwenden:',
    pickOnTopics: 'Aufgaben auswählen zu',
    startEmpty: 'Leer anfangen',
    sameWeekPastYears: 'Dieselbe Woche in früheren Jahren',
    suggestedExercises: 'Vorgeschlagene Aufgaben',
    typicalTopics: 'Typische Themen',
    useAsStart: 'Als Ausgangspunkt nehmen',
    noCalendar: 'Dieses Semester hat noch keinen Kalender, also haben die Wochen keine Daten. Eine Admin-Person legt ihn auf der Admin-Seite fest.',
    introSteps: ['Aufgaben finden', 'Auf einem Blatt anordnen', 'Die PDFs für Studierende und mit Lösungen drucken'],
  },
});

export const SHEETS_VIEW_LABELS = localized('SHEETS_VIEW_LABELS', {
  en: {
    list: 'List',
    weeks: 'Weeks',
    table: 'Table',
    bin: WEEK_LABELS_EN.bin,
    pastTermReadOnly: 'An earlier term: read-only here. Duplicate a sheet to reuse it this term.',
    setTerm: 'Set term',
    setWeek: 'Set week',
    setStatus: 'Set status',
    exportZips: 'Export',
    duplicate: 'Duplicate',
  },
  de: {
    list: 'Liste',
    weeks: 'Wochen',
    table: 'Tabelle',
    bin: WEEK_LABELS_DE.bin,
    pastTermReadOnly: 'Ein früheres Semester: hier nur lesbar. Dupliziere ein Blatt, um es in diesem Semester wiederzuverwenden.',
    setTerm: 'Semester setzen',
    setWeek: 'Woche setzen',
    setStatus: 'Status setzen',
    exportZips: 'Exportieren',
    duplicate: 'Duplizieren',
  },
});

/** A change of status as an action — where a bare "Status: Ready" would read as the state. */
export const STATUS_ACTIONS: Record<SheetStatus, string> = localized('STATUS_ACTIONS', {
  en: {
    draft: 'Mark as draft',
    ready: 'Mark ready',
    published: 'Mark published',
  },
  de: {
    draft: 'Als Entwurf markieren',
    ready: 'Als fertig markieren',
    published: 'Als veröffentlicht markieren',
  },
});

/** What each status means, for the pill's choices and tooltips. */
export const STATUS_HINTS: Record<SheetStatus, string> = localized('STATUS_HINTS', {
  en: {
    draft: 'Still being worked on.',
    ready: 'Checked and ready to hand out.',
    published: 'Handed out: published to the course (by its owner, an admin or a merged review).',
  },
  de: {
    draft: 'Wird noch bearbeitet.',
    ready: 'Geprüft und bereit zum Austeilen.',
    published: 'Ausgeteilt: im Kurs veröffentlicht (von der Person, der es gehört, einer Admin-Person oder durch ein gemergtes Review).',
  },
});

/** The Review page, usage in the library and the same-term check (Phase 6, Lane R). */
export const REVIEW_LABELS = localized('REVIEW_LABELS', {
  en: {
    problemsTab: L_EN.problems,
    readyTab: 'Ready to hand out',
    /** the tabs' names on a phone */
    problemsShort: 'Problems',
    readyShort: 'Ready',
    readyHint: 'Sheets marked Ready, by week. Check the PDFs, then mark them published once handed out.',
    markPublished: 'Mark published',
    onlyUsedThisTerm: 'Only on this term’s sheets',
    editReport: 'Edit',
    notUsedThisTerm: 'Not used this term',
    usedIn: 'Used in',
    usedInHint: 'The sheets in Sheet Studio this exercise is on (sheets in the bin are left out).',
    notUsedYet: 'Not on any sheet in Sheet Studio yet.',
    sameTerm: 'Already used this term',
  },
  de: {
    problemsTab: L_DE.problems,
    readyTab: 'Bereit zum Austeilen',
    problemsShort: 'Probleme',
    readyShort: 'Fertig',
    readyHint: 'Als fertig markierte Blätter, nach Woche. Prüfe die PDFs und markiere sie als veröffentlicht, sobald sie ausgeteilt sind.',
    markPublished: 'Als veröffentlicht markieren',
    onlyUsedThisTerm: 'Nur auf Blättern dieses Semesters',
    editReport: 'Bearbeiten',
    notUsedThisTerm: 'In diesem Semester nicht verwendet',
    usedIn: 'Verwendet in',
    usedInHint: 'Die Blätter in Sheet Studio, auf denen diese Aufgabe steht (Blätter im Papierkorb nicht mitgezählt).',
    notUsedYet: 'Noch auf keinem Blatt in Sheet Studio.',
    sameTerm: 'In diesem Semester schon verwendet',
  },
});

/** The Admin page (Phase 6, Lane A) — a power page: English only. */
export const ADMIN_LABELS = {
  page: WEEK_LABELS_EN.admin,
  pageHint: 'People and their roles, the bin, the term calendar, backups and the server’s housekeeping.',
  overview: 'Overview',
  users: 'Users',
  bin: WEEK_LABELS_EN.bin,
  term: 'Term calendar',
  operations: 'Backups & cache',
  roleOverride: 'Override',
  overrideHint: 'An override outlives the next CMS sign-in until you clear it. Addresses in STUDIO_ADMIN_EMAILS are always admins.',
  deactivate: 'Deactivate',
  reactivate: 'Reactivate',
  deactivated: 'Deactivated',
  deactivateHint: 'They are signed out at once, their API tokens stop working, and they cannot sign in again until reactivated.',
  transfer: 'Transfer ownership…',
  transferHint: 'Everything moves in one step. Sheets in the bin move too: they still belong to the account.',
  deleteBlocked: 'Transfer their sheets and exercises first — deleting the account would delete them.',
  backupNow: 'Back up now',
  clearOldRenders: 'Clear old renders',
  runNow: 'Run now',
  resetToCourse: 'Reset to the course config',
  apiTokens: 'API tokens',
  jobs: 'Scheduled jobs',
} as const;

/** Similar exercises and "Match: meaning" (Phase 7, Lane S). */
export const SIMILAR_LABELS = localized('SIMILAR_LABELS', {
  en: {
    tab: 'Similar',
    moreLikeThis: 'More like this',
    moreLikeThisHint: 'Exercises like this one, in the Add tab',
    moreLike: (title: string) => `More like “${title}”`,
    backToSearch: 'Back to the search',
    intro: 'Exercises that read alike — the same words in English or German, the same code, the same topics. Its own other versions are listed apart.',
    method: 'Matched on words, code and topics (no AI model; nothing leaves the server).',
    none: 'Nothing similar enough was found.',
    noneHint: 'Exercises with very little text, or on a topic nobody else wrote about, have no close neighbours.',
    otherVersions: 'Other versions of this exercise',
    translation: 'other language',
    revision: 'other wording',
    shares: 'shares',
    close: 'very similar',
    related: 'similar',
    loose: 'loosely related',
    loadFailed: 'Could not load similar exercises',
    tryAgain: 'Try again',
    pick: 'Pick',
    picked: 'Picked',
    match: 'Match',
    matchWords: 'words',
    matchMeaning: 'meaning',
    matchWordsHint: 'Find the words you typed (and words starting with them)',
    matchMeaningHint: 'Find exercises about what you typed: English and German wording, related words, code names and topics count — ranked by closeness',
    showSimilar: 'Show similar exercises',
    alikeInTerm: (n: number, year: string) =>
      `${n === 1 ? 'One exercise is' : `${n} exercises are`} very like one already on another sheet of ${year}.`,
    matchCommandWords: 'Search by words',
    matchCommandMeaning: 'Search by meaning (both languages, related words)',
  },
  de: {
    tab: 'Ähnlich',
    moreLikeThis: 'Mehr davon',
    moreLikeThisHint: 'Aufgaben wie diese, im Tab Hinzufügen',
    moreLike: (title: string) => `Mehr wie „${title}“`,
    backToSearch: 'Zurück zur Suche',
    intro: 'Aufgaben, die sich ähnlich lesen — dieselben Wörter auf Englisch oder Deutsch, derselbe Code, dieselben Themen. Ihre eigenen anderen Fassungen stehen getrennt.',
    method: 'Verglichen über Wörter, Code und Themen (kein KI-Modell; nichts verlässt den Server).',
    none: 'Nichts ausreichend Ähnliches gefunden.',
    noneHint: 'Aufgaben mit sehr wenig Text oder zu einem Thema, über das sonst niemand geschrieben hat, haben keine nahen Nachbarn.',
    otherVersions: 'Andere Fassungen dieser Aufgabe',
    translation: 'andere Sprache',
    revision: 'andere Formulierung',
    shares: 'gemeinsam',
    close: 'sehr ähnlich',
    related: 'ähnlich',
    loose: 'entfernt verwandt',
    loadFailed: 'Ähnliche Aufgaben konnten nicht geladen werden',
    tryAgain: 'Erneut versuchen',
    pick: 'Auswählen',
    picked: 'Ausgewählt',
    match: 'Suche nach',
    matchWords: 'Wörtern',
    matchMeaning: 'Bedeutung',
    matchWordsHint: 'Findet die eingegebenen Wörter (und Wörter, die mit ihnen beginnen)',
    matchMeaningHint: 'Findet Aufgaben zu dem, was du eingibst: englische und deutsche Formulierungen, verwandte Wörter, Code-Namen und Themen zählen — nach Nähe sortiert',
    showSimilar: 'Ähnliche Aufgaben zeigen',
    alikeInTerm: (n: number, year: string) =>
      `${n === 1 ? 'Eine Aufgabe ist' : `${n} Aufgaben sind`} einer Aufgabe auf einem anderen Blatt von ${year} sehr ähnlich.`,
    matchCommandWords: 'Nach Wörtern suchen',
    matchCommandMeaning: 'Nach Bedeutung suchen (beide Sprachen, verwandte Wörter)',
  },
});
